import styled from '@emotion/styled'
import { TFunction } from 'i18next'
import { withTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import type { TypedDispatch } from '../../..'
import { saveTowingFormState } from '../../../actions/api'
import { isTruthy } from '../../../lib/generalUtils'
import moment from '../../../lib/moment-fi'
import { formStates, stepStates } from '../../../lib/towingUtils'
import { getColor, theme } from '../../../Theme'
import type { AppState } from '../../../types'
import { TowingFormStateChange, TowingFormStepOrSelection } from '../../../types/Input'
import type { TowingFormReduxState } from '../../../types/States'
import TowingConfirmButton from './TowingConfirmButton'
import TowingConfirmToNext from './TowingConfirmToNext'
import { getValidCounterSteps } from './TowingCounterStepList'

interface TowingConfirmContainerProps {
  elements: any[]
  counterElements?: any[]
  elementStates: TowingFormStepOrSelection
  towingFormState: TowingFormReduxState
  towingConfirmed: Partial<TowingFormStateChange> | null | undefined
  confirmForm: (towingFormState: TowingFormReduxState, formType?: string) => unknown
  formType?: string
  t: TFunction
  saving: boolean
}

const Container = styled.div`
  padding-bottom: ${theme.spacing.sizes.small};
  margin-right: ${theme.spacing.sizes.small};
  margin-left: ${theme.spacing.sizes.small};
  margin-top: ${theme.spacing.sizes.large};
  margin-bottom: ${theme.spacing.sizes.tiny};
  & + & {
    margin-top: ${theme.spacing.sizes.smaller};
  }
  display: flex;
  flex-direction: row;
  gap: 20px;
`

const StepHeaderText = styled.div`
  color: ${(p) => getColor(p.theme, ['black'], ['white'])};
  padding-left: 30px;
  padding-top: 10px;
  padding-bottom: 10px;
  flex-grow: 1;
  font-weight: bold;
`

const getSelectedElements = (el: any, elementStates: TowingFormStepOrSelection) => {
  const namePattern = /^name[\d]+$/
  const names = Object.keys(el)
    .filter((key) => namePattern.test(key))
    .map((name) => ({ name, branch: name.replace('name', 'branch') }))
  const selectedBranch = names.find(
    (name) => el[name.branch] && elementStates[el.key] === name.branch
  )
  const selectedElements = selectedBranch && el[selectedBranch.branch].elements

  return {
    selectedElements,
    selectedBranch,
    names,
  }
}

const getBranchSelectedElements = (
  el: any,
  elementStates: TowingFormStepOrSelection,
  includeTowingSelection?: boolean
) => {
  const { selectedElements } = getSelectedElements(el, elementStates)
  if (selectedElements && selectedElements.length > 0) {
    return getTowingFlatList(selectedElements, elementStates, includeTowingSelection)
  }
}

const getUnSelectedBranchElements = (el: any, elementStates: TowingFormStepOrSelection) => {
  const { selectedElements, names } = getSelectedElements(el, elementStates)
  if (selectedElements && selectedElements.length > 0) {
    const unSelectedBranches = names.filter(
      (name) => el[name.branch] && elementStates[el.key] !== name.branch
    )
    return unSelectedBranches
      .flatMap(
        (name) => getValidCounterSteps(el[name.branch].elements, selectedElements, elementStates),
        elementStates
      )
      .concat(
        getCounterActionsForUnselectedBranchesFlatList(
          selectedElements.filter((el: any) => el.contentType === 'towingSelection'),
          elementStates
        )
      )
      .concat(
        unSelectedBranches.flatMap((name) =>
          getCounterActionsForUnselectedBranchesFlatList(
            el[name.branch].elements.filter((el: any) => el.contentType === 'towingSelection'),
            elementStates
          )
        )
      )
  }
}

const getFlatCounterList = (elements: any[], elementStates: TowingFormStepOrSelection): any[] =>
  elements.flatMap((el: any) => {
    if (el.contentType === 'towingSelection' && elementStates[el.key]) {
      const namePattern = /^name[\d]+$/
      const names = Object.keys(el)
        .filter((key) => namePattern.test(key))
        .map((name) => ({ name, branch: name.replace('name', 'branch') }))
      const selectedBranch = names.find(
        (name) => el[name.branch] && elementStates[el.key] === name.branch
      )
      const selectedElements = selectedBranch && el[selectedBranch.branch].elements
      if (selectedElements && selectedElements.length > 0) {
        return getFlatCounterList(selectedElements, elementStates)
      }
    } else if (el.hasCounterAction) {
      return el
    }

    return undefined
  })

export const getTowingFlatListWithTowingSelection = (
  elements: any[],
  elementStates: TowingFormStepOrSelection
) => {
  return getTowingFlatList(elements, elementStates, true)
}

export const getTowingFlatList = (
  elements: any[],
  elementStates: TowingFormStepOrSelection,
  includeTowingSelection?: boolean
): any[] =>
  elements.flatMap((el: any) => {
    if (el.contentType === 'towingSelection' && elementStates[el.key]) {
      if (includeTowingSelection) {
        return (getBranchSelectedElements(el, elementStates, includeTowingSelection) ?? []).concat(
          el
        )
      } else {
        return getBranchSelectedElements(el, elementStates, includeTowingSelection)
      }
    }
    return el
  })

const getCounterActionsForUnselectedBranchesFlatList = (
  elements: any[],
  elementStates: TowingFormStepOrSelection
): any[] =>
  elements.flatMap((el) => {
    if (el.contentType === 'towingSelection' && elementStates[el.key]) {
      return getUnSelectedBranchElements(el, elementStates)
    }

    return undefined
  })

const getTowingFunctionState = (elements: any[], elementStates: TowingFormStepOrSelection) => {
  const enabled = true
  for (let i = 0; i < elements.length; i++) {
    if (elements[i].contentType === 'towingFunction' && elements[i].type === 'TRANSITION_TO_NEXT')
      return enabled
    const key = elements[i].key
    if (
      elements[i].mandatory &&
      elements[i].contentType !== 'towingContent' &&
      (!elementStates[key] ||
        (elements[i].contentType !== 'towingSelection' && elementStates[key] !== stepStates.DONE))
    ) {
      return false
    }
  }
  return enabled
}

const getTowingFunctionStateForCounterSteps = (
  elements: any[],
  elementStates: TowingFormStepOrSelection
) => {
  for (let i = 0; i < elements.length; i++) {
    const key = elements[i].key
    if (
      elements[i].mandatory &&
      elements[i].contentType !== 'towingContent' &&
      elementStates[key] === stepStates.DONE
    ) {
      return false
    }
  }
  return true
}

const getCounterActionsReady = (
  elements: any[],
  elementStates: TowingFormStepOrSelection,
  isFinishOrStop: boolean
) => {
  for (let i = 0; i < elements.length; i++) {
    const key = elements[i].key
    if (
      elements[i].mandatory &&
      (!isFinishOrStop || elements[i].counterActionMandatoryInFinish) &&
      elementStates[key] === stepStates.DONE
    ) {
      return false
    }
  }
  return true
}

const getTowingReadyState = (elements: any[], elementStates: TowingFormStepOrSelection) => {
  for (let i = 0; i < elements.length; i++) {
    const key = elements[i].key
    if (
      (elements[i].mandatory || elements[i].contentType === 'towingSelection') &&
      (!elementStates[key] ||
        (elements[i].contentType !== 'towingSelection' &&
          elementStates[key] !== stepStates.DONE)) &&
      elements[i].contentType !== 'towingFunction'
    ) {
      return false
    }
  }
  return true
}

const getTowingConfirmedText = (
  formType: string | undefined,
  towingConfirmed: Partial<TowingFormStateChange>,
  t: TFunction
) => {
  const changedBy = towingConfirmed.changedBy
  const changedAt = moment(towingConfirmed.changedAt).format('DD.MM.YYYY HH:mm')
  if (formType === 'setup') {
    // TODO: handle situations where personnel data is removed, add translations
    return changedBy && changedBy !== ''
      ? t('confirmedTowingSetup', { changedBy, changedAt })
      : t('confirmedTowingSetupWithoutPersonnel', { changedAt })
  } else if (formType === 'start') {
    return changedBy && changedBy !== ''
      ? t('confirmedTowingStart', { changedBy, changedAt })
      : t('confirmedTowingStartWithoutPersonnel', { changedAt })
  } else if (formType === 'stop') {
    return changedBy && changedBy !== ''
      ? t('confirmedTowingStop', { changedBy, changedAt })
      : t('confirmedTowingStopWithoutPersonnel', { changedAt })
  } else if (formType === 'finish') {
    return changedBy && changedBy !== ''
      ? t('confirmedTowingFinish', { changedBy, changedAt })
      : t('confirmedTowingFinishWithoutPersonnel', { changedAt })
  }
}

const TowingConfirmContainer = ({
  elements,
  counterElements,
  elementStates,
  towingFormState,
  towingConfirmed,
  confirmForm,
  formType,
  t,
  saving,
}: TowingConfirmContainerProps) => {
  const handleConfirmForm = () => {
    confirmForm(towingFormState, formType)
  }

  const towingFlatList = getTowingFlatList(elements, elementStates).filter(isTruthy)
  const towingFunctionList = towingFlatList.filter((el) => {
    if (el) {
      return el.contentType === 'towingFunction' && el.type === 'TRANSITION_TO_NEXT'
    }
    return false
  })
  const towingFunction =
    towingFunctionList.length > 0 ? towingFunctionList.reduce((p, c) => p || c) : undefined
  const towingBranchCounterActionFlatList = getCounterActionsForUnselectedBranchesFlatList(
    elements,
    elementStates
  ).filter(isTruthy)
  let towingReady =
    getTowingReadyState(elements, elementStates) &&
    getTowingReadyState(towingFlatList, elementStates) &&
    getCounterActionsReady(towingBranchCounterActionFlatList, elementStates, false)
  if (counterElements) {
    const flatCounterList = getFlatCounterList(counterElements, elementStates).filter(isTruthy)
    towingReady = towingReady && getCounterActionsReady(flatCounterList, elementStates, true)
  }

  const handOverDone = towingFormState.stateChanges.find((el) => el.status === formStates.HANDOVER)

  return (
    <>
      {towingConfirmed ? (
        <Container>
          <StepHeaderText>{getTowingConfirmedText(formType, towingConfirmed, t)}</StepHeaderText>
        </Container>
      ) : (
        <>
          {towingFunction && handOverDone && (
            <Container>
              <StepHeaderText>
                {`${handOverDone.changedBy} kuitannut valmis seuraavalle ${moment(
                  handOverDone.changedAt
                ).format('DD.MM.YYYY HH:mm')}`}
              </StepHeaderText>
            </Container>
          )}
          <Container>
            {towingFunction && (
              <TowingConfirmToNext
                key={towingFunction.name || towingFunction.key}
                enabled={
                  !saving &&
                  !handOverDone &&
                  !towingReady &&
                  getTowingFunctionState(towingFlatList, elementStates) &&
                  getTowingFunctionStateForCounterSteps(
                    towingBranchCounterActionFlatList,
                    elementStates
                  )
                }
              />
            )}
            <TowingConfirmButton
              buttonText={getConfirmText(t, formType)}
              disabled={saving || !towingReady}
              onClick={handleConfirmForm}
            />
          </Container>
        </>
      )}
    </>
  )
}

const getConfirmText = (t: TFunction, formType?: string) => {
  if (formType === 'stop') {
    return `${t('confirmTowingStepForStop')}`
  } else if (formType === 'finish') {
    return t('confirmTowingStepForFinish')
  } else {
    return `${t('confirmTowingStepDefault')}`
  }
}

const mapStateToProps = (state: AppState, { formType }: { formType?: string }) => {
  const elementStates = state.towingFormState.actionsAndSelectionsByKey
  const towingFormState = state.towingFormState
  const stateChangesLatestFirst =
    state.towingFormState.stateChanges &&
    state.towingFormState.stateChanges.slice().sort((firstEl, secondEl) => {
      return moment(secondEl.changedAt).diff(moment(firstEl.changedAt))
    })
  const saving = state.towingFormState.saving

  const getTowingConfirmed = (
    stateChanges: TowingFormStateChange[],
    formType?: string
  ): TowingFormStateChange | null | undefined => {
    const setupDoneIdx = stateChanges.findIndex(
      (stateChange) => stateChange.status === formStates.SETUP_DONE
    )
    const inSetupIdx = stateChanges.findIndex(
      (stateChange) => stateChange.status === formStates.IN_SETUP
    )
    const setupDone =
      (setupDoneIdx >= 0 &&
        (setupDoneIdx < inSetupIdx || inSetupIdx < 0) &&
        stateChanges[setupDoneIdx]) ||
      null
    const startDone = stateChanges.find((el) => el.status === formStates.IN_TOWING)
    const stopInProgress = stateChanges.find((el) => el.status === formStates.STOP_IN_PROGRESS)
    const finished = stateChanges.find((el) => el.status === formStates.FINISHED)

    if (formType === 'setup') {
      return setupDone
    }
    if (
      formType === 'start' &&
      startDone &&
      setupDone &&
      moment(startDone.changedAt).isAfter(moment(setupDone.changedAt))
    )
      return startDone
    if (formType === 'stop' && stopInProgress) {
      const stopInProgressIndex = stateChangesLatestFirst.findIndex(
        (s) => s.status === formStates.STOP_IN_PROGRESS
      )
      if (
        stopInProgressIndex > 0 &&
        stateChangesLatestFirst[stopInProgressIndex - 1].status === formStates.SETUP_DONE
      )
        return stateChangesLatestFirst[stopInProgressIndex - 1]
    }
    if (formType === 'finish') return finished
  }
  const towingConfirmed = stateChangesLatestFirst?.length
    ? getTowingConfirmed(stateChangesLatestFirst, formType)
    : {}
  return {
    elementStates,
    towingFormState,
    towingConfirmed,
    saving,
  }
}

const mapDispatchToProps = (dispatch: TypedDispatch) => ({
  confirmForm: (towingFormState: TowingFormReduxState, formType?: string) =>
    dispatch(saveTowingFormState(towingFormState, formType)),
})

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(TowingConfirmContainer)
)
